import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  IRetryPolicy,
  LogLevel,
} from '@microsoft/signalr';
import { MessagePackHubProtocol } from '@microsoft/signalr-protocol-msgpack';
import type {
  SignalRConnectionOptions,
  ConnectionState,
  SignalRClientMethods,
  RealTimeEventHandlers,
} from '../types/signalr';
import { appConfig } from '../config/app';

class CustomRetryPolicy implements IRetryPolicy {
  private readonly retryDelays = [0, 2000, 5000, 10000, 20000, 30000];

  nextRetryDelayInMilliseconds(retryContext: {
    previousRetryCount: number;
    elapsedMilliseconds: number;
  }): number | null {
    const { previousRetryCount } = retryContext;
    
    // Stop retrying after 6 attempts
    if (previousRetryCount >= this.retryDelays.length) {
      return null;
    }
    
    return this.retryDelays[previousRetryCount];
  }
}

export class SignalRService {
  private connection: HubConnection | null = null;
  private connectionOptions: SignalRConnectionOptions;
  private eventHandlers: Map<keyof SignalRClientMethods, Set<Function>> = new Map();
  private connectionStateHandlers: Set<(state: ConnectionState) => void> = new Set();
  private isConnecting = false;
  private reconnectAttempts = 0;
  private lastConnected?: Date;
  private currentError?: string;

  constructor(options: SignalRConnectionOptions) {
    this.connectionOptions = {
      automaticReconnect: true,
      keepAliveIntervalInMilliseconds: 15000,
      serverTimeoutInMilliseconds: 30000,
      enableMessagePack: true,
      enableDetailedErrors: false,
      skipNegotiation: false,
      withCredentials: true,
      reconnectPolicy: [0, 2000, 5000, 10000, 20000, 30000],
      ...options,
    };

    this.buildConnection();
  }

  private buildConnection(): void {
    if (this.connection) {
      this.connection.stop();
    }

    const builder = new HubConnectionBuilder()
      .withUrl(this.connectionOptions.url, {
        accessTokenFactory: this.connectionOptions.accessTokenFactory,
        skipNegotiation: this.connectionOptions.skipNegotiation,
        withCredentials: this.connectionOptions.withCredentials,
      })
      .configureLogging(
        this.connectionOptions.enableDetailedErrors
          ? LogLevel.Debug
          : LogLevel.Information
      );

    // Add MessagePack protocol for better performance
    if (this.connectionOptions.enableMessagePack) {
      builder.withHubProtocol(new MessagePackHubProtocol());
    }

    // Configure automatic reconnect
    if (this.connectionOptions.automaticReconnect) {
      if (this.connectionOptions.reconnectPolicy) {
        builder.withAutomaticReconnect(this.connectionOptions.reconnectPolicy);
      } else {
        builder.withAutomaticReconnect(new CustomRetryPolicy());
      }
    }

    this.connection = builder.build();

    // Set up connection event handlers
    this.setupConnectionEvents();
  }

  private setupConnectionEvents(): void {
    if (!this.connection) return;

    this.connection.onclose((error) => {
      this.currentError = error?.message;
      this.notifyConnectionStateChange('Disconnected');
      this.notifyEventHandlers('onDisconnected', error || undefined);
    });

    this.connection.onreconnecting((error) => {
      this.reconnectAttempts++;
      this.currentError = error?.message;
      this.notifyConnectionStateChange('Reconnecting');
      this.notifyEventHandlers('onReconnecting');
    });

    this.connection.onreconnected((connectionId) => {
      this.reconnectAttempts = 0;
      this.currentError = undefined;
      this.lastConnected = new Date();
      this.notifyConnectionStateChange('Connected');
      this.notifyEventHandlers('onReconnected');
      
      // Re-register all event handlers
      this.registerEventHandlers();
    });

    // Register initial event handlers
    this.registerEventHandlers();
  }

  private registerEventHandlers(): void {
    if (!this.connection) return;

    // Register all client methods that can be called by the server
    const clientMethods: (keyof SignalRClientMethods)[] = [
      'RealTimeEvent',
      'PresenceUpdate',
      'TypingIndicator',
      'SystemNotification',
      'LiveMetrics',
      'ActivityFeedUpdate',
      'CollaborationEvent',
      'BulkOperationProgress',
      'ConnectionStatus',
      'Error',
      // Migration-specific methods
      'MigrationProgress',
      'MigrationComplete',
      'MigrationError',
      'MigrationCancelled',
      'EntityProgress',
    ];

    clientMethods.forEach((methodName) => {
      this.connection!.on(methodName, (...args: any[]) => {
        const handlers = this.eventHandlers.get(methodName);
        if (handlers) {
          handlers.forEach((handler) => handler(...args));
        }
        
        // Also notify generic event handlers
        this.notifyEventHandlers(`on${methodName}` as keyof RealTimeEventHandlers, ...args);
      });
    });
  }

  private notifyConnectionStateChange(state: ConnectionState): void {
    this.connectionStateHandlers.forEach((handler) => handler(state));
  }

  private notifyEventHandlers(eventName: keyof RealTimeEventHandlers, ...args: any[]): void {
    // This would be handled by the context or hooks using this service
  }

  public async connect(): Promise<void> {
    if (this.isConnecting || this.getConnectionState() === 'Connected') {
      return;
    }

    if (!this.connection) {
      this.buildConnection();
    }

    try {
      this.isConnecting = true;
      this.notifyConnectionStateChange('Connecting');
      
      await this.connection!.start();
      
      this.lastConnected = new Date();
      this.currentError = undefined;
      this.reconnectAttempts = 0;
      this.notifyConnectionStateChange('Connected');
      this.notifyEventHandlers('onConnected');
    } catch (error) {
      this.currentError = error instanceof Error ? error.message : 'Connection failed';
      this.notifyConnectionStateChange('Disconnected');
      this.notifyEventHandlers('onError', this.currentError);
      throw error;
    } finally {
      this.isConnecting = false;
    }
  }

  public async disconnect(): Promise<void> {
    if (!this.connection || this.connection.state === HubConnectionState.Disconnected) {
      return;
    }

    try {
      this.notifyConnectionStateChange('Disconnecting');
      await this.connection.stop();
      this.notifyConnectionStateChange('Disconnected');
    } catch (error) {
      this.currentError = error instanceof Error ? error.message : 'Disconnect failed';
      this.notifyEventHandlers('onError', this.currentError);
      throw error;
    }
  }

  public getConnectionState(): ConnectionState {
    if (!this.connection) return 'Disconnected';

    switch (this.connection.state) {
      case HubConnectionState.Connected:
        return 'Connected';
      case HubConnectionState.Connecting:
        return 'Connecting';
      case HubConnectionState.Reconnecting:
        return 'Reconnecting';
      case HubConnectionState.Disconnecting:
        return 'Disconnecting';
      case HubConnectionState.Disconnected:
      default:
        return 'Disconnected';
    }
  }

  public isConnected(): boolean {
    return this.connection?.state === HubConnectionState.Connected;
  }

  public getConnectionInfo() {
    return {
      connectionState: this.getConnectionState(),
      isConnected: this.isConnected(),
      lastConnected: this.lastConnected,
      reconnectAttempts: this.reconnectAttempts,
      error: this.currentError,
      connectionId: this.connection?.connectionId,
    };
  }

  // Hub method invocations
  public async joinTicketGroup(ticketId: string): Promise<void> {
    if (!this.isConnected()) {
      throw new Error('SignalR connection is not established');
    }
    
    try {
      await this.connection!.invoke('JoinTicketGroup', ticketId);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to join ticket group';
      this.notifyEventHandlers('onError', message);
      throw error;
    }
  }

  public async leaveTicketGroup(ticketId: string): Promise<void> {
    if (!this.isConnected()) {
      throw new Error('SignalR connection is not established');
    }
    
    try {
      await this.connection!.invoke('LeaveTicketGroup', ticketId);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to leave ticket group';
      this.notifyEventHandlers('onError', message);
      throw error;
    }
  }

  public async startTyping(ticketId: string): Promise<void> {
    if (!this.isConnected()) return;
    
    try {
      await this.connection!.invoke('StartTyping', ticketId);
    } catch (error) {
      console.warn('Failed to send typing indicator:', error);
    }
  }

  public async stopTyping(ticketId: string): Promise<void> {
    if (!this.isConnected()) return;
    
    try {
      await this.connection!.invoke('StopTyping', ticketId);
    } catch (error) {
      console.warn('Failed to stop typing indicator:', error);
    }
  }

  public async updatePresence(status: string, currentTicketId?: string): Promise<void> {
    if (!this.isConnected()) return;
    
    try {
      await this.connection!.invoke('UpdatePresence', status, currentTicketId);
    } catch (error) {
      console.warn('Failed to update presence:', error);
    }
  }

  // Event subscription methods
  public on<K extends keyof SignalRClientMethods>(
    methodName: K,
    handler: SignalRClientMethods[K]
  ): void {
    if (!this.eventHandlers.has(methodName)) {
      this.eventHandlers.set(methodName, new Set());
    }
    this.eventHandlers.get(methodName)!.add(handler as Function);
  }

  public off<K extends keyof SignalRClientMethods>(
    methodName: K,
    handler?: SignalRClientMethods[K]
  ): void {
    const handlers = this.eventHandlers.get(methodName);
    if (handlers) {
      if (handler) {
        handlers.delete(handler as Function);
      } else {
        handlers.clear();
      }
    }
  }

  public onConnectionStateChanged(handler: (state: ConnectionState) => void): void {
    this.connectionStateHandlers.add(handler);
  }

  public offConnectionStateChanged(handler: (state: ConnectionState) => void): void {
    this.connectionStateHandlers.delete(handler);
  }

  // Cleanup
  public dispose(): void {
    this.eventHandlers.clear();
    this.connectionStateHandlers.clear();
    
    if (this.connection) {
      this.connection.stop();
      this.connection = null;
    }
  }
}

// Factory function to create SignalR service instance
export const createSignalRService = (accessToken: string): SignalRService => {
  const options: SignalRConnectionOptions = {
    url: `${appConfig.apiBaseUrl.replace('/api/v1', '')}/supportdeskhub`,
    accessTokenFactory: () => accessToken,
    automaticReconnect: true,
    enableMessagePack: true,
    enableDetailedErrors: appConfig.environment === 'development',
    withCredentials: true,
  };

  return new SignalRService(options);
};

// Factory function to create migration-specific SignalR service instance
export const createMigrationSignalRService = (accessToken: string): SignalRService => {
  const options: SignalRConnectionOptions = {
    url: `${appConfig.apiBaseUrl.replace('/api/v1', '')}/migrationhub`,
    accessTokenFactory: () => accessToken,
    automaticReconnect: true,
    enableMessagePack: true,
    enableDetailedErrors: appConfig.environment === 'development',
    withCredentials: true,
  };

  return new SignalRService(options);
};

// Singleton instance for global use
let globalSignalRService: SignalRService | null = null;

export const getSignalRService = (accessToken?: string): SignalRService => {
  if (!globalSignalRService && accessToken) {
    globalSignalRService = createSignalRService(accessToken);
  }
  
  if (!globalSignalRService) {
    throw new Error('SignalR service not initialized. Please provide an access token.');
  }
  
  return globalSignalRService;
};

export const disposeSignalRService = (): void => {
  if (globalSignalRService) {
    globalSignalRService.dispose();
    globalSignalRService = null;
  }
};